'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdCalculate,
    MdCheckCircle,
    MdPayments,
    MdCancel,
    MdAttachMoney,
    MdExpandMore,
    MdExpandLess,
    MdPictureAsPdf,
    MdEmail,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import ConfirmModal from '@/components/confirm-modal'
import {
    getPayrollRun,
    calculatePayroll,
    approvePayroll,
    markPayrollPaid,
    cancelPayroll,
    generatePayrollPayables,
    sendPayslips,
    downloadPayslipPdf,
    HrPayrollRun,
    HrPayrollItem,
} from '@/lib/api/hr/hr-payroll-api'

/** Status da conta a pagar do salário, na coluna "Pagamento". */
const PAYABLE_BADGES: Record<string, string> = {
    PENDING: 'badge-warning',
    PAID: 'badge-success',
    CANCELLED: 'badge-error',
    REFUNDED: 'badge-error',
    OVERDUE: 'badge-error',
}

const PAYABLE_LABELS: Record<string, string> = {
    PENDING: 'Aguardando',
    PAID: 'Pago',
    CANCELLED: 'Cancelado',
    REFUNDED: 'Estornado',
    OVERDUE: 'Vencido',
}

const STATUS_BADGES: Record<string, string> = {
    DRAFT: 'badge-ghost',
    CALCULATING: 'badge-info',
    CALCULATED: 'badge-warning',
    APPROVED: 'badge-success',
    PAID: 'badge-secondary',
    CANCELLED: 'badge-error',
}

const STATUS_LABELS: Record<string, string> = {
    DRAFT: 'Rascunho',
    CALCULATING: 'Calculando',
    CALCULATED: 'Calculada',
    APPROVED: 'Aprovada',
    PAID: 'Paga',
    CANCELLED: 'Cancelada',
}

const TYPE_LABELS: Record<string, string> = {
    MONTHLY: 'Mensal',
    ADVANCE: 'Adiantamento',
    THIRTEENTH_1ST: '13o 1a Parcela',
    THIRTEENTH_2ND: '13o 2a Parcela',
    VACATION: 'Férias',
    TERMINATION: 'Rescisão',
    COMPLEMENTARY: 'Complementar',
}

/** Mesmo padrão do backend: dia 5 do mês seguinte à competência. */
const defaultPayrollDueDate = (competence: string) => {
    const [year, month] = competence.split('-').map(Number)
    const due = new Date(Date.UTC(year, month, 5))
    return due.toISOString().slice(0, 10)
}

/** Hoje em Brasília, no formato do input de data. */
const todayInBrazil = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })

const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export default function PayrollRunDetailPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { canUpdate } = useResourcePermissions('hr', 'payroll')

    const [run, setRun] = useState<HrPayrollRun | null>(null)
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [expandedEmployee, setExpandedEmployee] = useState<number | null>(null)

    // Confirm modals
    const [showApproveModal, setShowApproveModal] = useState(false)
    const [showPayModal, setShowPayModal] = useState(false)
    const [showGeneratePayablesModal, setShowGeneratePayablesModal] = useState(false)
    const [payablesDueDate, setPayablesDueDate] = useState('')
    const [generatingPayables, setGeneratingPayables] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [showSendModal, setShowSendModal] = useState(false)
    const [sendingEmails, setSendingEmails] = useState(false)
    const [downloadingPdf, setDownloadingPdf] = useState<number | null>(null)

    useEffect(() => {
        if (id) loadRun()
    }, [id])

    const loadRun = async () => {
        if (!id) return
        setLoading(true)
        try {
            const res = await getPayrollRun(parseInt(id, 10))
            if (res.success && res.result) {
                setRun(res.result)
            } else {
                toast.error(res.error || 'Erro ao carregar folha de pagamento')
                navigate('/hr/payroll')
            }
        } catch {
            toast.error('Erro ao carregar folha de pagamento')
            navigate('/hr/payroll')
        } finally {
            setLoading(false)
        }
    }

    const handleCalculate = async () => {
        if (!id) return
        setActionLoading(true)
        try {
            const res = await calculatePayroll(parseInt(id, 10))
            if (res.success) {
                toast.success('Folha calculada com sucesso')
                loadRun()
            } else {
                toast.error(res.error || 'Erro ao calcular folha')
            }
        } catch {
            toast.error('Erro ao calcular folha')
        } finally {
            setActionLoading(false)
        }
    }

    const handleApprove = async () => {
        if (!id) return
        setActionLoading(true)
        try {
            const res = await approvePayroll(parseInt(id, 10))
            if (res.success) {
                toast.success('Folha aprovada com sucesso')
                setShowApproveModal(false)
                loadRun()
            } else {
                toast.error(res.error || 'Erro ao aprovar folha')
            }
        } catch {
            toast.error('Erro ao aprovar folha')
        } finally {
            setActionLoading(false)
        }
    }

    const handlePay = async () => {
        if (!id) return
        setActionLoading(true)
        try {
            const res = await markPayrollPaid(parseInt(id, 10))
            if (res.success) {
                toast.success('Folha marcada como paga')
                setShowPayModal(false)
                loadRun()
            } else {
                toast.error(res.error || 'Erro ao marcar folha como paga')
            }
        } catch {
            toast.error('Erro ao marcar folha como paga')
        } finally {
            setActionLoading(false)
        }
    }

    const handleGeneratePayables = async () => {
        if (!id) return
        setGeneratingPayables(true)
        try {
            if (!payablesDueDate) {
                toast.warning('Informe a data de pagamento')
                return
            }
            const res = await generatePayrollPayables(parseInt(id, 10), { dueDate: payablesDueDate })
            if (res.success && res.result) {
                const { created, skipped, errors } = res.result
                if (created > 0) {
                    toast.success(
                        `${created} conta(s) a pagar gerada(s) e enviada(s) para aprovação financeira`,
                    )
                } else if (skipped.length > 0 && errors.length === 0) {
                    toast.info('Nenhuma conta nova: todos os funcionários já foram enviados')
                }
                errors.forEach(error => toast.error(error))
                setShowGeneratePayablesModal(false)
                loadRun()
            } else {
                toast.error(res.error || 'Erro ao enviar a folha para pagamento')
            }
        } catch {
            toast.error('Erro ao enviar a folha para pagamento')
        } finally {
            setGeneratingPayables(false)
        }
    }

    const handleCancel = async () => {
        if (!id) return
        setActionLoading(true)
        try {
            const res = await cancelPayroll(parseInt(id, 10))
            if (res.success) {
                toast.success('Folha cancelada com sucesso')
                setShowCancelModal(false)
                loadRun()
            } else {
                toast.error(res.error || 'Erro ao cancelar folha')
            }
        } catch {
            toast.error('Erro ao cancelar folha')
        } finally {
            setActionLoading(false)
        }
    }

    const handleSendPayslips = async () => {
        if (!id) return
        setSendingEmails(true)
        try {
            const res = await sendPayslips(parseInt(id, 10))
            if (res.success && res.result) {
                const { sent, errors } = res.result
                if (sent > 0) toast.success(`${sent} holerite(s) enviado(s) com sucesso`)
                if (errors.length > 0) {
                    errors.forEach(err => toast.warning(err))
                }
            } else {
                toast.error(res.error || 'Erro ao enviar holerites')
            }
        } catch {
            toast.error('Erro ao enviar holerites')
        } finally {
            setSendingEmails(false)
            setShowSendModal(false)
        }
    }

    const handleOpenPayslipPdf = async (employeeId: number) => {
        if (!id) return
        setDownloadingPdf(employeeId)
        try {
            await downloadPayslipPdf(parseInt(id, 10), employeeId)
        } catch {
            toast.error('Erro ao gerar holerite')
        } finally {
            setDownloadingPdf(null)
        }
    }

    const toggleEmployee = (employeeId: number) => {
        setExpandedEmployee(prev => (prev === employeeId ? null : employeeId))
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando folha de pagamento...</p>
            </div>
        )
    }

    if (!run) return null

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/hr/payroll')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdPayments className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Folha {run.competence} - {TYPE_LABELS[run.type] || run.type}
                            {/* Folha de outra filial abre normalmente; sem o nome
                                aqui, o operador não saberia de quem é o que está
                                aprovando. */}
                            {run.company?.name && (
                                <span className="ml-2 text-base font-normal text-base-content/60">
                                    {run.company.name}
                                </span>
                            )}
                        </h1>
                        <div className="flex items-center gap-2 mt-1">
                            <span className={`badge ${STATUS_BADGES[run.status] || 'badge-ghost'}`}>
                                {STATUS_LABELS[run.status] || run.status}
                            </span>
                            {run.origin === 'IMPORTED' && (
                                <span
                                    className="badge badge-outline badge-sm"
                                    title="Folha montada a partir dos holerites enviados pela contabilidade"
                                >
                                    Importada da contabilidade
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                {canUpdate && (
                    <div className="flex items-center gap-2 flex-wrap">
                        {run.status === 'DRAFT' && (
                            <button
                                onClick={handleCalculate}
                                className="btn btn-info btn-sm gap-2"
                                disabled={actionLoading}
                            >
                                <MdCalculate className="text-lg" />
                                Calcular
                            </button>
                        )}
                        {run.status === 'CALCULATED' && (
                            <button
                                onClick={() => setShowApproveModal(true)}
                                className="btn btn-success btn-sm gap-2"
                                disabled={actionLoading}
                            >
                                <MdCheckCircle className="text-lg" />
                                Aprovar
                            </button>
                        )}
                        {run.status === 'APPROVED' && (
                            <>
                                <button
                                    onClick={() => {
                                        // Vencimento no passado não é aceito: o padrão já
                                        // vencido (folha atrasada) vira hoje.
                                        const suggested = defaultPayrollDueDate(run.competence)
                                        const today = todayInBrazil()
                                        setPayablesDueDate(suggested < today ? today : suggested)
                                        setShowGeneratePayablesModal(true)
                                    }}
                                    className="btn btn-primary btn-sm gap-2"
                                    disabled={generatingPayables}
                                >
                                    {generatingPayables ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdPayments className="text-lg" />
                                    )}
                                    Enviar para Pagamento
                                </button>
                                <button
                                    onClick={() => setShowPayModal(true)}
                                    className="btn btn-secondary btn-sm gap-2"
                                    disabled={actionLoading}
                                >
                                    <MdAttachMoney className="text-lg" />
                                    Marcar como Paga
                                </button>
                            </>
                        )}
                        {run.status !== 'PAID' && run.status !== 'CANCELLED' && (
                            <button
                                onClick={() => setShowCancelModal(true)}
                                className="btn btn-error btn-sm gap-2"
                                disabled={actionLoading}
                            >
                                <MdCancel className="text-lg" />
                                Cancelar
                            </button>
                        )}
                        {run.status !== 'DRAFT' && run.status !== 'CANCELLED' && (
                            <button
                                onClick={() => setShowSendModal(true)}
                                className="btn btn-outline btn-sm gap-2"
                                disabled={sendingEmails}
                            >
                                <MdEmail className="text-lg" />
                                Enviar Holerites
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">Total Proventos</span>
                        <p className="text-xl font-bold text-success">{formatCurrency(run.totalEarnings)}</p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">Total Descontos</span>
                        <p className="text-xl font-bold text-error">{formatCurrency(run.totalDeductions)}</p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">Líquido</span>
                        <p className="text-xl font-bold text-secondary">{formatCurrency(run.totalNet)}</p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">FGTS</span>
                        <p className="text-xl font-bold">{formatCurrency(run.totalFgts)}</p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">INSS Patronal</span>
                        <p className="text-xl font-bold">{formatCurrency(run.totalInssCompany)}</p>
                    </div>
                </div>
            </div>

            {/* Notes */}
            {run.notes && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase mb-1">Observações</span>
                        <p className="p-3 bg-base-200/50 rounded-lg">{run.notes}</p>
                    </div>
                </div>
            )}

            {/* Employees Table */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">
                        Funcionários ({run.items?.length || 0})
                    </h2>

                    {!run.items || run.items.length === 0 ? (
                        <div className="text-center py-8 text-base-content/60">
                            <MdPayments className="text-4xl mx-auto mb-2 opacity-40" />
                            <p>Nenhum funcionário nesta folha</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Nome</th>
                                        <th>CPF</th>
                                        <th className="text-right">Salário Bruto</th>
                                        <th className="text-right">Proventos</th>
                                        <th className="text-right">Descontos</th>
                                        <th className="text-right">Líquido</th>
                                        <th className="text-right">FGTS</th>
                                        <th className="text-right">INSS</th>
                                        <th className="text-right">IRRF</th>
                                        <th>Pagamento</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {run.items.map((item: HrPayrollItem) => (
                                        <>
                                            <tr
                                                key={item.id}
                                                className="hover cursor-pointer"
                                                onClick={() => toggleEmployee(item.id)}
                                            >
                                                <td>
                                                    {expandedEmployee === item.id ? (
                                                        <MdExpandLess className="text-lg" />
                                                    ) : (
                                                        <MdExpandMore className="text-lg" />
                                                    )}
                                                </td>
                                                <td className="font-semibold">
                                                    {item.employee?.name || '-'}
                                                    {item.employee?.jobTitle && (
                                                        <div className="text-xs text-base-content/60">
                                                            {item.employee.jobTitle}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="font-mono text-sm">
                                                    {item.employee?.cpf || '-'}
                                                </td>
                                                <td className="text-right">{formatCurrency(item.grossSalary)}</td>
                                                <td className="text-right text-success">{formatCurrency(item.totalEarnings)}</td>
                                                <td className="text-right text-error">{formatCurrency(item.totalDeductions)}</td>
                                                <td className="text-right font-bold">{formatCurrency(item.netSalary)}</td>
                                                <td className="text-right">{formatCurrency(item.fgtsValue)}</td>
                                                <td className="text-right">{formatCurrency(item.inssValue)}</td>
                                                <td className="text-right">{formatCurrency(item.irrfValue)}</td>
                                                <td onClick={e => e.stopPropagation()}>
                                                    {item.payableBilling ? (
                                                        <button
                                                            type="button"
                                                            className={`badge badge-sm ${PAYABLE_BADGES[item.payableBilling.status] || 'badge-ghost'}`}
                                                            onClick={() =>
                                                                navigate(`/financial/billings/${item.payableBilling!.id}`)
                                                            }
                                                            title="Abrir a conta a pagar"
                                                        >
                                                            {PAYABLE_LABELS[item.payableBilling.status]
                                                                || item.payableBilling.status}
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-base-content/50">
                                                            Não enviado
                                                        </span>
                                                    )}
                                                    {item.employee?.platformOrganizationId && (
                                                        <div
                                                            className="text-xs text-base-content/60 mt-1"
                                                            title="Salário sai por transferência entre contas da plataforma, sem tarifa"
                                                        >
                                                            Conta da plataforma
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    {/* Folha importada só tem o PDF da contabilidade; sem ele não há holerite. */}
                                                    <button
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            handleOpenPayslipPdf(item.employeeId)
                                                        }}
                                                        className="btn btn-ghost btn-xs"
                                                        title={run.origin === 'IMPORTED' && !item.documentFileId
                                                            ? 'Sem holerite original da contabilidade'
                                                            : 'Imprimir Holerite'}
                                                        disabled={
                                                            downloadingPdf === item.employeeId
                                                            || (run.origin === 'IMPORTED' && !item.documentFileId)
                                                        }
                                                    >
                                                        {downloadingPdf === item.employeeId
                                                            ? <span className="loading loading-spinner loading-xs" />
                                                            : <MdPictureAsPdf className="text-base" />
                                                        }
                                                    </button>
                                                </td>
                                            </tr>
                                            {expandedEmployee === item.id && item.details && item.details.length > 0 && (
                                                <tr key={`detail-${item.id}`}>
                                                    <td colSpan={11} className="bg-base-200/30 p-0">
                                                        <div className="p-4">
                                                            <h4 className="text-sm font-semibold mb-2">Detalhamento de Rubricas</h4>
                                                            <table className="table table-xs">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Código</th>
                                                                        <th>Rubrica</th>
                                                                        <th>Tipo</th>
                                                                        <th>Referência</th>
                                                                        <th className="text-right">Valor</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {item.details.map(detail => (
                                                                        <tr key={detail.id}>
                                                                            <td className="font-mono text-xs">
                                                                                {detail.rubric?.code || '-'}
                                                                            </td>
                                                                            <td>{detail.rubric?.name || detail.description || '-'}</td>
                                                                            <td>
                                                                                <span className={`badge badge-xs ${detail.type === 'EARNING' ? 'badge-success' : detail.type === 'DEDUCTION' ? 'badge-error' : 'badge-info'}`}>
                                                                                    {detail.type === 'EARNING' ? 'Provento' : detail.type === 'DEDUCTION' ? 'Desconto' : 'Info'}
                                                                                </span>
                                                                            </td>
                                                                            <td>{detail.reference ?? '-'}</td>
                                                                            <td className={`text-right font-semibold ${detail.type === 'EARNING' ? 'text-success' : detail.type === 'DEDUCTION' ? 'text-error' : ''}`}>
                                                                                {formatCurrency(detail.value)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Confirm Modals */}
            <ConfirmModal
                isOpen={showApproveModal}
                onClose={() => setShowApproveModal(false)}
                onConfirm={handleApprove}
                title="Aprovar Folha de Pagamento"
                message="Tem certeza de que deseja aprovar esta folha? Após aprovação, os valores não poderão ser alterados e o holerite será enviado automaticamente por e-mail para cada funcionário."
                loading={actionLoading}
                confirmButtonText="Aprovar"
                cancelButtonText="Cancelar"
            />

            <ConfirmModal
                isOpen={showGeneratePayablesModal}
                onClose={() => setShowGeneratePayablesModal(false)}
                onConfirm={handleGeneratePayables}
                title="Enviar Folha para Pagamento"
                message="Será gerada uma conta a pagar por funcionário, com a despesa e o lançamento contábil, e todas irão para a fila de aprovação financeira. O dinheiro só sai quando o financeiro aprovar. Quem tem conta na plataforma recebe por transferência entre contas, sem tarifa; os demais, por PIX ou TED do cadastro."
                loading={generatingPayables}
                confirmButtonText="Enviar para Pagamento"
                cancelButtonText="Cancelar"
            >
                <div className="form-control w-full">
                    <label className="label pb-1" htmlFor="payables-due-date">
                        <span className="label-text font-medium">Data de pagamento</span>
                    </label>
                    <input
                        id="payables-due-date"
                        type="date"
                        className="input input-bordered w-full"
                        value={payablesDueDate}
                        min={todayInBrazil()}
                        onChange={e => setPayablesDueDate(e.target.value)}
                        disabled={generatingPayables}
                    />
                    <label className="label pt-1">
                        <span className="label-text-alt text-base-content/60">
                            Vencimento das contas a pagar. Sugerido: dia 5 do mês seguinte; antecipe quando cair em
                            feriado ou fim de semana.
                        </span>
                    </label>
                </div>
            </ConfirmModal>

            <ConfirmModal
                isOpen={showPayModal}
                onClose={() => setShowPayModal(false)}
                onConfirm={handlePay}
                title="Marcar como Paga"
                message="Use isto quando o pagamento tiver sido feito por fora do sistema — nenhuma transferência é disparada e a folha apenas fica registrada como paga. Para pagar pelo sistema, use “Enviar para Pagamento”."
                loading={actionLoading}
                confirmButtonText="Confirmar Pagamento"
                cancelButtonText="Cancelar"
            />

            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleCancel}
                title="Cancelar Folha de Pagamento"
                message="Tem certeza de que deseja cancelar esta folha? Esta ação não pode ser desfeita."
                loading={actionLoading}
                confirmButtonText="Cancelar Folha"
                cancelButtonText="Voltar"
                confirmButtonClass="btn-error"
            />

            <ConfirmModal
                isOpen={showSendModal}
                onClose={() => setShowSendModal(false)}
                onConfirm={handleSendPayslips}
                title="Enviar Holerites por E-mail"
                message="Os holerites serão enviados por e-mail para todos os funcionários que possuem e-mail cadastrado. Deseja continuar?"
                loading={sendingEmails}
                confirmButtonText="Enviar"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
